import { useState } from 'react';

/**
 * Design Preview Page
 * Shows 3 visual design alternatives for the Finova dashboard
 * Access at: /design-preview
 */

type ThemeOption = 'current' | 'polished' | 'dark' | 'gradient';

export function DesignPreview(): JSX.Element {
  const [activeTheme, setActiveTheme] = useState<ThemeOption>('current');

  const themes: { id: ThemeOption; name: string; description: string }[] = [
    { id: 'current', name: 'Current Design', description: 'Existing blue theme with borders' },
    { id: 'polished', name: 'Alternative 1: Polished Corporate', description: 'Refined light theme with shadows, teal accents' },
    { id: 'dark', name: 'Alternative 2: Professional Dark', description: 'Deep slate with glassmorphism effects' },
    { id: 'gradient', name: 'Alternative 3: Modern Gradient', description: 'Bold purple gradients, SaaS-style' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      {/* Theme Selector */}
      <div className="mx-auto max-w-7xl mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Design Alternatives Preview</h1>
        <p className="text-gray-600 mb-6">Compare different visual styles for the Finova Financial System</p>

        <div className="flex flex-wrap gap-3">
          {themes.map((theme) => (
            <button
              key={theme.id}
              onClick={() => setActiveTheme(theme.id)}
              className={`px-4 py-3 rounded-xl text-left transition-all ${
                activeTheme === theme.id
                  ? 'bg-blue-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 shadow'
              }`}
            >
              <div className="font-semibold">{theme.name}</div>
              <div className={`text-sm ${activeTheme === theme.id ? 'text-blue-100' : 'text-gray-500'}`}>
                {theme.description}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Preview Container */}
      <div className="mx-auto max-w-7xl">
        {activeTheme === 'current' && <CurrentThemePreview />}
        {activeTheme === 'polished' && <PolishedThemePreview />}
        {activeTheme === 'dark' && <DarkThemePreview />}
        {activeTheme === 'gradient' && <GradientThemePreview />}
      </div>
    </div>
  );
}

// ============================================================================
// CURRENT THEME (for comparison)
// ============================================================================

function CurrentThemePreview(): JSX.Element {
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl">
      {/* Sidebar + Content */}
      <div className="flex bg-white">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r min-h-[800px] p-4">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">A</div>
            <span className="font-bold text-gray-900">Finova</span>
          </div>

          <nav className="space-y-1">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Main</div>
            <a className="flex items-center gap-3 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 font-medium">
              <HomeIcon />
              Dashboard
            </a>
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-4">Accounts Payable</div>
            <a className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
              <FileIcon />
              Bills
              <span className="ml-auto bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full">12</span>
            </a>
            <a className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
              <UsersIcon />
              Vendors
            </a>
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase mt-4">Accounts Receivable</div>
            <a className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
              <FileIcon />
              Invoices
              <span className="ml-auto bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">8</span>
            </a>
            <a className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50">
              <UsersIcon />
              Customers
            </a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6 bg-gray-50">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome back, User</h1>
              <p className="text-gray-500">Here's your financial overview for January 2026</p>
            </div>
            <div className="flex gap-2">
              <button className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50">New Bill</button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">New Invoice</button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <KPICardCurrent title="Total Revenue" value="$1,245,890" change="+12.5%" positive icon="trending" color="green" />
            <KPICardCurrent title="Accounts Receivable" value="$89,450" change="+8.3%" positive icon="file" color="blue" />
            <KPICardCurrent title="Accounts Payable" value="$34,200" change="-5.2%" positive icon="bill" color="orange" />
            <KPICardCurrent title="Net Cash" value="$342,890" change="+15.7%" positive icon="cash" color="purple" />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="col-span-2 rounded-xl border bg-white p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Cash Flow</h3>
                  <p className="text-sm text-gray-500">6-month overview</p>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500"></span> Inflows</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-400"></span> Outflows</span>
                </div>
              </div>
              <ChartPlaceholder height={200} />
            </div>
            <div className="space-y-4">
              <AgingCardCurrent title="Receivables Aging" total="$89,450" />
              <AgingCardCurrent title="Payables Aging" total="$34,200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICardCurrent({ title, value, change, positive, icon: _icon, color }: { title: string; value: string; change: string; positive: boolean; icon: string; color: string }): JSX.Element {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="rounded-xl border bg-white p-5">
      <div className="flex justify-between items-start mb-3">
        <span className="text-sm text-gray-500">{title}</span>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <TrendIcon />
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className={`text-sm mt-1 ${positive ? 'text-green-600' : 'text-red-500'}`}>{change} vs last month</p>
    </div>
  );
}

function AgingCardCurrent({ title, total }: { title: string; total: string }): JSX.Element {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex justify-between mb-2">
        <span className="font-semibold text-gray-900">{title}</span>
        <span className="font-medium">{total}</span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 flex overflow-hidden mb-3">
        <div className="bg-green-500 w-[45%]"></div>
        <div className="bg-yellow-500 w-[25%]"></div>
        <div className="bg-orange-500 w-[15%]"></div>
        <div className="bg-red-500 w-[15%]"></div>
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-gray-500">Current</span><span>$40,252</span></div>
        <div className="flex justify-between"><span className="text-gray-500">1-30 days</span><span>$22,362</span></div>
        <div className="flex justify-between"><span className="text-gray-500">31-60 days</span><span>$13,418</span></div>
        <div className="flex justify-between"><span className="text-gray-500">60+ days</span><span>$13,418</span></div>
      </div>
    </div>
  );
}

// ============================================================================
// ALTERNATIVE 1: POLISHED CORPORATE (Light, Refined, Teal Accent)
// ============================================================================

function PolishedThemePreview(): JSX.Element {
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl">
      <div className="flex" style={{ background: '#F8FAFC' }}>
        {/* Sidebar - Clean white with subtle shadows */}
        <div className="w-72 bg-white min-h-[800px] p-5 shadow-xl">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-teal-200">A</div>
            <div>
              <span className="font-bold text-slate-800 text-lg">Finova</span>
              <p className="text-xs text-slate-400">Financial System</p>
            </div>
          </div>

          <nav className="space-y-1">
            <div className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Overview</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-medium shadow-lg shadow-teal-100">
              <HomeIcon />
              Dashboard
            </a>
            <div className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider mt-6">Accounts Payable</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-all">
              <FileIcon />
              Bills
              <span className="ml-auto bg-rose-50 text-rose-600 text-xs px-2.5 py-1 rounded-full font-semibold">12</span>
            </a>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-all">
              <UsersIcon />
              Vendors
            </a>
            <div className="px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider mt-6">Accounts Receivable</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-all">
              <FileIcon />
              Invoices
              <span className="ml-auto bg-teal-50 text-teal-600 text-xs px-2.5 py-1 rounded-full font-semibold">8</span>
            </a>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-50 transition-all">
              <UsersIcon />
              Customers
            </a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-800">Good morning, User</h1>
              <p className="text-slate-500 mt-1">Here's your financial snapshot for January 2026</p>
            </div>
            <div className="flex gap-3">
              <button className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 font-medium hover:bg-slate-50 shadow-sm transition-all">
                + New Bill
              </button>
              <button className="px-5 py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-teal-200 transition-all">
                + New Invoice
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-5 mb-8">
            <KPICardPolished title="Total Revenue (YTD)" value="$1,245,890" change="+12.5%" color="emerald" />
            <KPICardPolished title="Accounts Receivable" value="$89,450" change="+8.3%" color="cyan" subtitle="3 overdue" />
            <KPICardPolished title="Accounts Payable" value="$34,200" change="-5.2%" color="amber" subtitle="2 overdue" />
            <KPICardPolished title="Net Cash Position" value="$342,890" change="+15.7%" color="violet" />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="col-span-2 rounded-2xl bg-white p-6 shadow-lg shadow-slate-100">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-bold text-slate-800 text-lg">Cash Flow Overview</h3>
                  <p className="text-sm text-slate-400">Last 6 months performance</p>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> <span className="text-slate-500">Inflows</span></span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-rose-400"></span> <span className="text-slate-500">Outflows</span></span>
                </div>
              </div>
              <ChartPlaceholderPolished height={200} />
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100">
                <div><p className="text-sm text-slate-400">Total Inflows</p><p className="text-xl font-bold text-emerald-600">$603,000</p></div>
                <div><p className="text-sm text-slate-400">Total Outflows</p><p className="text-xl font-bold text-rose-500">$467,000</p></div>
                <div><p className="text-sm text-slate-400">Net Cash Flow</p><p className="text-xl font-bold text-cyan-600">$136,000</p></div>
              </div>
            </div>
            <div className="space-y-5">
              <AgingCardPolished title="Receivables Aging" total="$89,450" />
              <AgingCardPolished title="Payables Aging" total="$34,200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICardPolished({ title, value, change, color, subtitle }: { title: string; value: string; change: string; color: string; subtitle?: string }): JSX.Element {
  const colors: Record<string, { bg: string; icon: string; text: string }> = {
    emerald: { bg: 'bg-emerald-50', icon: 'bg-emerald-500', text: 'text-emerald-600' },
    cyan: { bg: 'bg-cyan-50', icon: 'bg-cyan-500', text: 'text-cyan-600' },
    amber: { bg: 'bg-amber-50', icon: 'bg-amber-500', text: 'text-amber-600' },
    violet: { bg: 'bg-violet-50', icon: 'bg-violet-500', text: 'text-violet-600' },
  };
  const c = colors[color] ?? colors['teal'];

  return (
    <div className="rounded-2xl bg-white p-5 shadow-lg shadow-slate-100 hover:shadow-xl transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <span className="text-sm text-slate-500 font-medium">{title}</span>
        <div className={`p-2.5 rounded-xl ${c?.icon ?? 'bg-teal-500'} text-white shadow-lg`}>
          <TrendIcon />
        </div>
      </div>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-sm font-semibold text-emerald-600">{change}</span>
        <span className="text-xs text-slate-400">vs last month</span>
      </div>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

function AgingCardPolished({ title, total }: { title: string; total: string }): JSX.Element {
  return (
    <div className="rounded-2xl bg-white p-5 shadow-lg shadow-slate-100">
      <div className="flex justify-between mb-3">
        <span className="font-bold text-slate-800">{title}</span>
        <span className="font-bold text-slate-800">{total}</span>
      </div>
      <div className="h-3 rounded-full bg-slate-100 flex overflow-hidden mb-4">
        <div className="bg-emerald-500 w-[45%]"></div>
        <div className="bg-amber-400 w-[25%]"></div>
        <div className="bg-orange-500 w-[15%]"></div>
        <div className="bg-rose-500 w-[15%]"></div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Current</span><span className="font-semibold text-slate-700">$40,252</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400"></span>1-30 days</span><span className="font-semibold text-slate-700">$22,362</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-500"></span>31-60 days</span><span className="font-semibold text-slate-700">$13,418</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-rose-500"></span>60+ days</span><span className="font-semibold text-slate-700">$13,418</span></div>
      </div>
    </div>
  );
}

function ChartPlaceholderPolished({ height }: { height: number }): JSX.Element {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  const inflows = [85, 92, 78, 105, 118, 125];
  const outflows = [62, 71, 68, 82, 89, 95];

  return (
    <div className="flex items-end gap-6" style={{ height }}>
      {months.map((month, i) => (
        <div key={month} className="flex-1 flex flex-col items-center gap-2">
          <div className="flex items-end gap-1 w-full justify-center">
            <div className="w-4 rounded-t-lg bg-gradient-to-t from-emerald-400 to-emerald-500 transition-all hover:from-emerald-500 hover:to-emerald-400" style={{ height: `${(inflows[i] ?? 0) * 1.4}px` }}></div>
            <div className="w-4 rounded-t-lg bg-gradient-to-t from-rose-300 to-rose-400 transition-all hover:from-rose-400 hover:to-rose-300" style={{ height: `${(outflows[i] ?? 0) * 1.4}px` }}></div>
          </div>
          <span className="text-xs text-slate-400 font-medium">{month}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// ALTERNATIVE 2: PROFESSIONAL DARK (Slate, Glassmorphism, Vibrant Accents)
// ============================================================================

function DarkThemePreview(): JSX.Element {
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl">
      <div className="flex" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        {/* Sidebar - Dark with glass effect */}
        <div className="w-72 min-h-[800px] p-5" style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(20px)', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-blue-500/30">A</div>
            <div>
              <span className="font-bold text-white text-lg">Finova</span>
              <p className="text-xs text-slate-400">Financial System</p>
            </div>
          </div>

          <nav className="space-y-1">
            <div className="px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Overview</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-white font-medium" style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              <HomeIcon />
              Dashboard
            </a>
            <div className="px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-6">Accounts Payable</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 transition-all">
              <FileIcon />
              Bills
              <span className="ml-auto bg-rose-500/20 text-rose-400 text-xs px-2.5 py-1 rounded-full font-semibold border border-rose-500/30">12</span>
            </a>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 transition-all">
              <UsersIcon />
              Vendors
            </a>
            <div className="px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-6">Accounts Receivable</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 transition-all">
              <FileIcon />
              Invoices
              <span className="ml-auto bg-blue-500/20 text-blue-400 text-xs px-2.5 py-1 rounded-full font-semibold border border-blue-500/30">8</span>
            </a>
            <a className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-300 hover:bg-white/5 transition-all">
              <UsersIcon />
              Customers
            </a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">Good evening, User</h1>
              <p className="text-slate-400 mt-1">Here's your financial snapshot for January 2026</p>
            </div>
            <div className="flex gap-3">
              <button className="px-5 py-2.5 rounded-xl text-slate-300 font-medium transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                + New Bill
              </button>
              <button className="px-5 py-2.5 text-white rounded-xl font-medium transition-all" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)' }}>
                + New Invoice
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-5 mb-8">
            <KPICardDark title="Total Revenue (YTD)" value="$1,245,890" change="+12.5%" gradient="from-emerald-500/20 to-teal-500/20" border="border-emerald-500/30" iconBg="bg-emerald-500" />
            <KPICardDark title="Accounts Receivable" value="$89,450" change="+8.3%" gradient="from-blue-500/20 to-cyan-500/20" border="border-blue-500/30" iconBg="bg-blue-500" subtitle="3 overdue" />
            <KPICardDark title="Accounts Payable" value="$34,200" change="-5.2%" gradient="from-amber-500/20 to-orange-500/20" border="border-amber-500/30" iconBg="bg-amber-500" subtitle="2 overdue" />
            <KPICardDark title="Net Cash Position" value="$342,890" change="+15.7%" gradient="from-violet-500/20 to-purple-500/20" border="border-violet-500/30" iconBg="bg-violet-500" />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="col-span-2 rounded-2xl p-6" style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-bold text-white text-lg">Cash Flow Overview</h3>
                  <p className="text-sm text-slate-400">Last 6 months performance</p>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> <span className="text-slate-400">Inflows</span></span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-rose-400"></span> <span className="text-slate-400">Outflows</span></span>
                </div>
              </div>
              <ChartPlaceholderDark height={200} />
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
                <div><p className="text-sm text-slate-400">Total Inflows</p><p className="text-xl font-bold text-emerald-400">$603,000</p></div>
                <div><p className="text-sm text-slate-400">Total Outflows</p><p className="text-xl font-bold text-rose-400">$467,000</p></div>
                <div><p className="text-sm text-slate-400">Net Cash Flow</p><p className="text-xl font-bold text-blue-400">$136,000</p></div>
              </div>
            </div>
            <div className="space-y-5">
              <AgingCardDark title="Receivables Aging" total="$89,450" />
              <AgingCardDark title="Payables Aging" total="$34,200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICardDark({ title, value, change, gradient, border, iconBg, subtitle }: { title: string; value: string; change: string; gradient: string; border: string; iconBg: string; subtitle?: string }): JSX.Element {
  return (
    <div className={`rounded-2xl p-5 bg-gradient-to-br ${gradient} ${border} border`} style={{ backdropFilter: 'blur(20px)' }}>
      <div className="flex justify-between items-start mb-4">
        <span className="text-sm text-slate-400 font-medium">{title}</span>
        <div className={`p-2.5 rounded-xl ${iconBg} text-white shadow-lg`}>
          <TrendIcon />
        </div>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-sm font-semibold text-emerald-400">{change}</span>
        <span className="text-xs text-slate-500">vs last month</span>
      </div>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

function AgingCardDark({ title, total }: { title: string; total: string }): JSX.Element {
  return (
    <div className="rounded-2xl p-5" style={{ background: 'rgba(30, 41, 59, 0.5)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <div className="flex justify-between mb-3">
        <span className="font-bold text-white">{title}</span>
        <span className="font-bold text-white">{total}</span>
      </div>
      <div className="h-3 rounded-full bg-slate-700 flex overflow-hidden mb-4">
        <div className="bg-emerald-500 w-[45%]"></div>
        <div className="bg-amber-400 w-[25%]"></div>
        <div className="bg-orange-500 w-[15%]"></div>
        <div className="bg-rose-500 w-[15%]"></div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-slate-400"><span className="w-2 h-2 rounded-full bg-emerald-500"></span>Current</span><span className="font-semibold text-slate-200">$40,252</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-slate-400"><span className="w-2 h-2 rounded-full bg-amber-400"></span>1-30 days</span><span className="font-semibold text-slate-200">$22,362</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-slate-400"><span className="w-2 h-2 rounded-full bg-orange-500"></span>31-60 days</span><span className="font-semibold text-slate-200">$13,418</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-slate-400"><span className="w-2 h-2 rounded-full bg-rose-500"></span>60+ days</span><span className="font-semibold text-slate-200">$13,418</span></div>
      </div>
    </div>
  );
}

function ChartPlaceholderDark({ height }: { height: number }): JSX.Element {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  const inflows = [85, 92, 78, 105, 118, 125];
  const outflows = [62, 71, 68, 82, 89, 95];

  return (
    <div className="flex items-end gap-6" style={{ height }}>
      {months.map((month, i) => (
        <div key={month} className="flex-1 flex flex-col items-center gap-2">
          <div className="flex items-end gap-1 w-full justify-center">
            <div className="w-4 rounded-t-lg bg-gradient-to-t from-emerald-600 to-emerald-400 transition-all hover:from-emerald-500 hover:to-emerald-300" style={{ height: `${(inflows[i] ?? 0) * 1.4}px` }}></div>
            <div className="w-4 rounded-t-lg bg-gradient-to-t from-rose-600 to-rose-400 transition-all hover:from-rose-500 hover:to-rose-300" style={{ height: `${(outflows[i] ?? 0) * 1.4}px` }}></div>
          </div>
          <span className="text-xs text-slate-500 font-medium">{month}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// ALTERNATIVE 3: MODERN GRADIENT (Bold Purple, SaaS-style)
// ============================================================================

function GradientThemePreview(): JSX.Element {
  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl">
      <div className="flex" style={{ background: 'linear-gradient(180deg, #FAF5FF 0%, #F3E8FF 50%, #EDE9FE 100%)' }}>
        {/* Sidebar - Gradient with bold colors */}
        <div className="w-72 min-h-[800px] p-5" style={{ background: 'linear-gradient(180deg, #7C3AED 0%, #5B21B6 100%)' }}>
          <div className="flex items-center gap-3 mb-10">
            <div className="h-10 w-10 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-lg">A</div>
            <div>
              <span className="font-bold text-white text-lg">Finova</span>
              <p className="text-xs text-purple-200">Financial System</p>
            </div>
          </div>

          <nav className="space-y-1">
            <div className="px-3 py-2 text-[11px] font-semibold text-purple-200 uppercase tracking-wider">Overview</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/20 backdrop-blur text-white font-medium">
              <HomeIcon />
              Dashboard
            </a>
            <div className="px-3 py-2 text-[11px] font-semibold text-purple-200 uppercase tracking-wider mt-6">Accounts Payable</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-2xl text-purple-100 hover:bg-white/10 transition-all">
              <FileIcon />
              Bills
              <span className="ml-auto bg-rose-500 text-white text-xs px-2.5 py-1 rounded-full font-semibold">12</span>
            </a>
            <a className="flex items-center gap-3 px-4 py-3 rounded-2xl text-purple-100 hover:bg-white/10 transition-all">
              <UsersIcon />
              Vendors
            </a>
            <div className="px-3 py-2 text-[11px] font-semibold text-purple-200 uppercase tracking-wider mt-6">Accounts Receivable</div>
            <a className="flex items-center gap-3 px-4 py-3 rounded-2xl text-purple-100 hover:bg-white/10 transition-all">
              <FileIcon />
              Invoices
              <span className="ml-auto bg-emerald-500 text-white text-xs px-2.5 py-1 rounded-full font-semibold">8</span>
            </a>
            <a className="flex items-center gap-3 px-4 py-3 rounded-2xl text-purple-100 hover:bg-white/10 transition-all">
              <UsersIcon />
              Customers
            </a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-purple-900">Welcome back, User</h1>
              <p className="text-purple-600 mt-1">Here's your financial snapshot for January 2026</p>
            </div>
            <div className="flex gap-3">
              <button className="px-5 py-2.5 bg-white rounded-2xl text-purple-700 font-medium shadow-lg shadow-purple-100 hover:shadow-xl transition-all border border-purple-100">
                + New Bill
              </button>
              <button className="px-5 py-2.5 text-white rounded-2xl font-medium shadow-lg shadow-purple-300 hover:shadow-xl transition-all" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' }}>
                + New Invoice
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-5 mb-8">
            <KPICardGradient title="Total Revenue (YTD)" value="$1,245,890" change="+12.5%" gradient="from-emerald-400 to-teal-500" />
            <KPICardGradient title="Accounts Receivable" value="$89,450" change="+8.3%" gradient="from-blue-400 to-indigo-500" subtitle="3 overdue" />
            <KPICardGradient title="Accounts Payable" value="$34,200" change="-5.2%" gradient="from-amber-400 to-orange-500" subtitle="2 overdue" />
            <KPICardGradient title="Net Cash Position" value="$342,890" change="+15.7%" gradient="from-violet-400 to-purple-600" />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-3 gap-6 mb-6">
            <div className="col-span-2 rounded-3xl bg-white p-6 shadow-xl shadow-purple-100">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-bold text-purple-900 text-lg">Cash Flow Overview</h3>
                  <p className="text-sm text-purple-400">Last 6 months performance</p>
                </div>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500"></span> <span className="text-purple-500">Inflows</span></span>
                  <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-rose-400 to-pink-500"></span> <span className="text-purple-500">Outflows</span></span>
                </div>
              </div>
              <ChartPlaceholderGradient height={200} />
              <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-purple-100">
                <div><p className="text-sm text-purple-400">Total Inflows</p><p className="text-xl font-bold text-emerald-600">$603,000</p></div>
                <div><p className="text-sm text-purple-400">Total Outflows</p><p className="text-xl font-bold text-rose-500">$467,000</p></div>
                <div><p className="text-sm text-purple-400">Net Cash Flow</p><p className="text-xl font-bold text-violet-600">$136,000</p></div>
              </div>
            </div>
            <div className="space-y-5">
              <AgingCardGradient title="Receivables Aging" total="$89,450" />
              <AgingCardGradient title="Payables Aging" total="$34,200" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICardGradient({ title, value, change, gradient, subtitle }: { title: string; value: string; change: string; gradient: string; subtitle?: string }): JSX.Element {
  return (
    <div className="rounded-3xl bg-white p-5 shadow-xl shadow-purple-100 hover:shadow-2xl hover:-translate-y-1 transition-all">
      <div className="flex justify-between items-start mb-4">
        <span className="text-sm text-purple-500 font-medium">{title}</span>
        <div className={`p-2.5 rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg`}>
          <TrendIcon />
        </div>
      </div>
      <p className="text-3xl font-bold text-purple-900">{value}</p>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-sm font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{change}</span>
        <span className="text-xs text-purple-400">vs last month</span>
      </div>
      {subtitle && <p className="text-xs text-purple-400 mt-1">{subtitle}</p>}
    </div>
  );
}

function AgingCardGradient({ title, total }: { title: string; total: string }): JSX.Element {
  return (
    <div className="rounded-3xl bg-white p-5 shadow-xl shadow-purple-100">
      <div className="flex justify-between mb-3">
        <span className="font-bold text-purple-900">{title}</span>
        <span className="font-bold text-purple-900">{total}</span>
      </div>
      <div className="h-3 rounded-full bg-purple-50 flex overflow-hidden mb-4">
        <div className="bg-gradient-to-r from-emerald-400 to-teal-500 w-[45%]"></div>
        <div className="bg-gradient-to-r from-amber-400 to-orange-400 w-[25%]"></div>
        <div className="bg-gradient-to-r from-orange-400 to-rose-400 w-[15%]"></div>
        <div className="bg-gradient-to-r from-rose-400 to-pink-500 w-[15%]"></div>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-purple-500"><span className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500"></span>Current</span><span className="font-semibold text-purple-800">$40,252</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-purple-500"><span className="w-2 h-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-400"></span>1-30 days</span><span className="font-semibold text-purple-800">$22,362</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-purple-500"><span className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-400 to-rose-400"></span>31-60 days</span><span className="font-semibold text-purple-800">$13,418</span></div>
        <div className="flex justify-between items-center"><span className="flex items-center gap-2 text-purple-500"><span className="w-2 h-2 rounded-full bg-gradient-to-r from-rose-400 to-pink-500"></span>60+ days</span><span className="font-semibold text-purple-800">$13,418</span></div>
      </div>
    </div>
  );
}

function ChartPlaceholderGradient({ height }: { height: number }): JSX.Element {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  const inflows = [85, 92, 78, 105, 118, 125];
  const outflows = [62, 71, 68, 82, 89, 95];

  return (
    <div className="flex items-end gap-6" style={{ height }}>
      {months.map((month, i) => (
        <div key={month} className="flex-1 flex flex-col items-center gap-2">
          <div className="flex items-end gap-1 w-full justify-center">
            <div className="w-5 rounded-t-xl transition-all hover:scale-105" style={{ height: `${(inflows[i] ?? 0) * 1.4}px`, background: 'linear-gradient(180deg, #34D399 0%, #14B8A6 100%)' }}></div>
            <div className="w-5 rounded-t-xl transition-all hover:scale-105" style={{ height: `${(outflows[i] ?? 0) * 1.4}px`, background: 'linear-gradient(180deg, #F472B6 0%, #EC4899 100%)' }}></div>
          </div>
          <span className="text-xs text-purple-400 font-medium">{month}</span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Shared Components
// ============================================================================

function ChartPlaceholder({ height }: { height: number }): JSX.Element {
  const months = ['Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
  const inflows = [85, 92, 78, 105, 118, 125];
  const outflows = [62, 71, 68, 82, 89, 95];

  return (
    <div className="flex items-end gap-4" style={{ height }}>
      {months.map((month, i) => (
        <div key={month} className="flex-1 flex flex-col items-center gap-2">
          <div className="flex items-end gap-1 w-full justify-center">
            <div className="w-4 rounded-t bg-green-500 transition-all hover:bg-green-400" style={{ height: `${(inflows[i] ?? 0) * 1.4}px` }}></div>
            <div className="w-4 rounded-t bg-red-400 transition-all hover:bg-red-300" style={{ height: `${(outflows[i] ?? 0) * 1.4}px` }}></div>
          </div>
          <span className="text-xs text-gray-500">{month}</span>
        </div>
      ))}
    </div>
  );
}

function HomeIcon(): JSX.Element {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function FileIcon(): JSX.Element {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function UsersIcon(): JSX.Element {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function TrendIcon(): JSX.Element {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}
